/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { ENoteStatus } from '@blend-types/eclose';
import { CommonAPI } from '../common/responses';
import { FeedbackIssueType, RoomStatus, SignAheadDocsStatus } from '@shared/constants/room';

export const baseUrl = `/api/internal/notary`;

export namespace NotaryAPI {
  export interface RoomDetailResponse {
    id: string;
    status: string;
    createdAt: string;
    scheduleDate: string;
    scheduleTime?: string;
    timeZone?: string;
    state: string;
    externalOrganization: string;
    signers: string[];
    notaryName: string | null;
    groupName: string;
    grouId: string;
    loanNumber?: string;
    loanType?: string;
    applicationId?: string;
    mailingAddress?: string;
    closingParties?: ClosingParty[];
    featureFlags: { id: string; enabled: string }[];
  }

  export interface Signer {
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    title: string;
  }

  export interface NotaryTask {
    name: string;
    status: string;
  }

  export interface RoomListItem {
    id: string;
    scheduleDate: string;
    scheduleTime: string;
    preferredTimeSlots: string[] | null;
    loanNumber?: string;
    timeZone: string | null;
    createdAt: string;
    signers: Signer[];
    notaryTasks?: NotaryTask[];
    state: string;
    externalOrganization?: string;
    notaryId: string | null;
    notaryName: string | null;
    status: RoomStatus;
  }

  export interface RoomListResponse {
    rooms: RoomListItem[];
    totalCount: number;
  }

  export const enum ClosingPartyType {
    SETTLEMENT = 'SETTLEMENT',
    CLOSER = 'CLOSER',
  }

  export interface ClosingParty {
    type?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email: string;
    agencyName?: string;
  }

  export interface RolesAndSignatures {
    id?: string;
    role: string;
    signature: string;
  }

  export interface PatchParticipantRequestBody {
    requiresKba?: boolean;
    title?: string;
    rolesAndSignatures?: RolesAndSignatures[];
  }

  export enum ParticipantSourceType {
    NOTARY_API = 'NOTARY_API',
    PROTECTED_API = 'PROTECTED_API',
  }

  export interface Participant {
    id: string;
    roomId: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    requiresKba: boolean;
    allowEditRequiresKba: boolean;
    title: string;
    signature: string;
    email?: string;
    phoneNumber?: string;
    sourceType: ParticipantSourceType;
    createdAt: string;
    rolesAndSignatures?: RolesAndSignatures[];
    allTitles?: string;
    primaryParticipantId?: string;
  }

  export interface ParticipantSessionForReviewResponse {
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    dateOfBirth: string;
    dateOfBirthFromLender?: string;
    issuanceDate: string;
    expirationDate: string;
    credentialNumber: string;
    identityDocumentImgTags: string[];
    idvReasons?: string[];
  }

  export type RoomParticipantsResponse = Participant[];

  export interface CallDetailsResponse extends CommonAPI.CallDetailsResponse {
    name: string;
    notaryEConsentedAt?: string;
    notaryVideoAccessToken?: string;
    documentsPendingTaggingNames?: string[];
  }

  export interface CallEvent {
    id: string;
    actor: string;
    action: string;
    description?: string;
    target: string;
    occurredAt: string;
  }
  export type CallEventsResponse = CallEvent[];

  export interface JournalEntryField {
    property: string;
    label: string;
    value?: any;
    type?: string;
    googleApiKey?: string;
  }

  export interface JournalEntryRecording {
    fileName: string;
    downloadUrl?: string;
    status: string;
  }

  export interface JournalEntryResponse {
    id: string;
    callId?: string;
    title?: string;
    appointmentInformation: JournalEntryField[];
    signers: JournalEntryField[][];
    recordings: JournalEntryRecording[] | null;
    notes: JournalEntryField;
    notaryLocation?: JournalEntryField;
  }

  export interface JournalEntryListItem {
    id: string;
    date: string;
    signers: string;
    description: string;
    location: string;
    state: string;
    status: string;
  }

  export interface JournalEntriesListResponse {
    results: JournalEntryListItem[];
    totalCount: number;
  }

  export interface UserResponse {
    id: string;
    firstName: string | null;
    lastName: string | null;
    address: CommonAPI.Address | null;
    permissions: BSRPermissions[];
    principalId: string;
    email: string;
  }

  export interface PendingActionResponse {
    unlockedBaseJournalEntryIds: string[];
    pendingCallRoomIds: string[];
  }

  export interface UserStatusResponse {
    isProfileComplete: boolean;
  }

  export interface CreateParticipantRequestBody {
    firstName: string;
    middleName?: string | null;
    lastName: string;
    suffix?: string | null;
    dateOfBirth?: string;
    title: string;
    email?: string;
    requiresKba: boolean;
    rolesAndSignatures?: object[];
  }

  export interface ScheduleCallRequestBody {
    localTime: string;
    timeZone: string;
    notaryId: string;
  }

  export interface CreateFeedbackRequestBody {
    satisfactionScore: number;
    userFeedback?: string;
    issues?: FeedbackIssueType[];
  }

  export enum BSRPermissions {
    BSRAdmin = 'BSRAdmin',
    BSRNotarization = 'BSRNotarization',
    BSRDocumentManagement = 'BSRDocumentManagement',
    BSRViewRoomDetails = 'BSRViewRoomDetails',
    BSRManageGenericParticipants = 'BSRManageGenericParticipants',
    BSRUpdateScheduleTime = 'BSRUpdateScheduleTime',
    BSRSuperAdmin = 'BSRSuperAdmin',
    BSRReviewNotary = 'BSRReviewNotary',
    BSRInviteNotary = 'BSRInviteNotary',
    BSRAssignRoomToGroup = 'BSRAssignRoomToGroup',
    BSRViewRoomList = 'BSRViewRoomList',
  }

  export interface Recording {
    callId: string;
    recordingId?: string;
    createdAt?: string;
    fileIdentifier?: string;
    duration?: number;
    status?: string;
    videoRoomId?: string;
  }

  export interface GetUserListResponse {
    users: UserResponse[];
    totalCount: number;
  }

  export interface OrganizationsResponse {
    organizations: string[];
  }

  export interface GroupsResponse {
    groups: GroupTuple[];
  }

  export interface GroupTuple {
    id: string;
    name: string;
  }

  export interface RoomStatusResponse {
    id: string;
    status: string;
  }

  export interface RoomMetadata {
    signAheadDocsStatus: SignAheadDocsStatus | null;
    eNoteStatus: ENoteStatus | null;
    sentSignAheadDocsToRON: boolean | null;
    signENoteInRON: boolean | null;
    specialInstructions: string | null;
  }

  export interface NotesResponse {
    id: string;
    roomId: string;
    notaryName: string;
    message: string;
    isComplete: boolean;
    createdAt: Date;
  }

  export interface CreateNoteRequestBody {
    message: string;
  }

  export interface NotaryTasksResponse {
    id: string;
    name: string;
    status: string;
  }

  export interface UpdateNotaryTaskRequestBody {
    status: string;
  }

  export interface SaveAdditionalVideoRoomIdRequestBody {
    newVideoRoomId: string;
  }

  export interface SaveAdditionVideoRoomIdResponse {
    additionalVideoRoomIds: string[];
  }
}
